import { useState, useEffect } from "react";
// material-ui
import { useTheme } from "@mui/material/styles";
import { Avatar, Box, useMediaQuery, Switch, Chip, Stack, Tooltip } from "@mui/material";
import VerifiedTwoToneIcon from "@mui/icons-material/VerifiedTwoTone";
import WorkspacePremiumTwoToneIcon from "@mui/icons-material/WorkspacePremiumTwoTone";
import DiamondTwoToneIcon from "@mui/icons-material/DiamondTwoTone";

// project imports
import LAYOUT_CONST from "constant";
import useConfig from "hooks/useConfig";
import LogoSection from "../LogoSection";
import SearchSection from "./SearchSection";
import MobileSection from "./MobileSection";
import ProfileSection from "./ProfileSection";
import LocalizationSection from "./LocalizationSection";
import MegaMenuSection from "./MegaMenuSection";
import NotificationSection from "./NotificationSection";

import { useDispatch, useSelector } from "store";
import { openDrawer } from "store/slices/menu";
import { getUserLocal, setUserLocal } from "utils/localStorage.util";
import WeeklySettlementApi from "api/weeklySettlement.api";
import { updateUser } from "redux/redux-slice/user.slice";

// assets
import { IconMenu2 } from "@tabler/icons";
import { inchargeNameChange } from "utils/common.utils";
import { getStaffId } from "store/constant";

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = () => {
  const theme = useTheme();

  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);
  const reduxUser = useSelector((state) => state.userData?.v_user_info);
  const localUser = getUserLocal();
  const userId = reduxUser || localUser;
  const isDSP = userId?.Type === "Division" || userId?.Type === "DSP" || userId?.Type === "State";

  const [kitInfo, setKitInfo] = useState({
    joiningKit: userId?.JoiningKit || null,
    isCommissionActive: Boolean(userId?.IsCommissionActive),
  });

  useEffect(() => {
    if (userId?.JoiningKit || userId?.IsCommissionActive !== undefined) {
      setKitInfo({
        joiningKit: userId?.JoiningKit || null,
        isCommissionActive: Boolean(userId?.IsCommissionActive),
      });
    }
  }, [userId?.JoiningKit, userId?.IsCommissionActive]);

  useEffect(() => {
    if (userId && userId.Type !== "Admin" && !isDSP) {
      const api = new WeeklySettlementApi();
      api
        .getMyKitStatus()
        .then((res) => {
          if (res?.data?.success && res.data.data) {
            const data = res.data.data;
            if (data.JoiningKit && data.IsCommissionActive) {
              setKitInfo({
                joiningKit: data.JoiningKit,
                isCommissionActive: Boolean(data.IsCommissionActive),
              });
              const updated = {
                ...userId,
                JoiningKit: data.JoiningKit,
                IsCommissionActive: Boolean(data.IsCommissionActive),
                WeeklyPayoutCap: data.WeeklyPayoutCap || 0,
              };
              setUserLocal(updated);
              dispatch(updateUser(updated));
            }
          }
        })
        .catch(() => {});
    }
  }, [userId?._id]);

  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const { layout } = useConfig();

  return (
    <>
      {/* logo & toggler button */}
      <Box
        sx={{
          width: 228,
          display: "flex",
          alignItems: "center",
          [theme.breakpoints.down("md")]: {
            width: "auto",
          },
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }}>
          <LogoSection type={userId?.Type} />
        </Box>

        {layout === LAYOUT_CONST.VERTICAL_LAYOUT ||
        (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              overflow: "hidden",
              transition: "all .2s ease-in-out",
              background:
                theme.palette.mode === "dark"
                  ? theme.palette.dark.main
                  : theme.palette.secondary.light,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.secondary.main
                  : theme.palette.secondary.dark,
              "&:hover": {
                background:
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.main
                    : theme.palette.secondary.dark,
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.light
                    : theme.palette.secondary.light,
              },
            }}
            onClick={() => dispatch(openDrawer(!drawerOpen))}
            color="inherit"
          >
            <IconMenu2 stroke={1.5} size="20px" />
          </Avatar>
        ) : null}
      </Box>
      {/* header search */}
      {/* <SearchSection /> */}
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ flexGrow: 1 }} />
      {/* mega-menu */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <div style={{ fontWeight: "bold", fontSize: "16px" }}>
            {inchargeNameChange(userId?.Type)}
          </div>
          {!isDSP && kitInfo?.isCommissionActive && kitInfo?.joiningKit && (
            <Tooltip title={`Commission Active: ${kitInfo.joiningKit.toUpperCase()} Plan`}>
              {kitInfo.joiningKit.toUpperCase() === "ROYAL" ? (
                <Chip
                  icon={<DiamondTwoToneIcon sx={{ fontSize: "16px !important", color: "#b45309 !important" }} />}
                  label="ROYAL"
                  size="small"
                  sx={{
                    bgcolor: "rgba(245, 158, 11, 0.15)",
                    color: "#b45309",
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    height: "22px",
                    border: "1px solid #f59e0b",
                    boxShadow: "0 0 8px rgba(245, 158, 11, 0.25)",
                  }}
                />
              ) : kitInfo.joiningKit.toUpperCase() === "PREMIUM" ? (
                <Chip
                  icon={<WorkspacePremiumTwoToneIcon sx={{ fontSize: "16px !important", color: "#6d28d9 !important" }} />}
                  label="PREMIUM"
                  size="small"
                  sx={{
                    bgcolor: "rgba(139, 92, 246, 0.15)",
                    color: "#6d28d9",
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    height: "22px",
                    border: "1px solid #8b5cf6",
                    boxShadow: "0 0 8px rgba(139, 92, 246, 0.25)",
                  }}
                />
              ) : (
                <Chip
                  icon={<VerifiedTwoToneIcon sx={{ fontSize: "16px !important", color: "#047857 !important" }} />}
                  label="BASIC"
                  size="small"
                  sx={{
                    bgcolor: "rgba(16, 185, 129, 0.15)",
                    color: "#047857",
                    fontWeight: 800,
                    fontSize: "0.7rem",
                    height: "22px",
                    border: "1px solid #10b981",
                    boxShadow: "0 0 8px rgba(16, 185, 129, 0.25)",
                  }}
                />
              )}
            </Tooltip>
          )}
        </Stack>
        <div style={{ fontSize: "13px", color: "gray" }}>ID: {getStaffId(userId?.StaffID)}</div>
      </Box>

      &nbsp;&nbsp;
      <Box sx={{ display: { xs: "none", sm: "block" } }}>{/* <MegaMenuSection /> */}</Box>
      {/* live customization & localization */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        {/* <LocalizationSection /> */}
        {/* <Switch onCh color="warning" /> */}
      </Box>
      {/* notification & profile */}
      <NotificationSection />
      <ProfileSection />
      {/* mobile header */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <MobileSection />
      </Box>
    </>
  );
};

export default Header;
